import { GetChannelsPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/inputs/get-channels-paginated.model';
import { useUrlSyncedFilters } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/hooks';
import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
} from 'react';
import { DeepPartialSkipArrayKey } from 'react-hook-form';

import {
  TABLE_LIMITS_OPTIONS,
  TABLE_LIMIT_OPTION_MIN,
} from '@app/config/app.config';
import {
  CHANNEL_FILTERS_LSK,
  CHANNEL_LIST_OPTIONS_LSK,
} from '@app/config/localstorage-keys.const';
import { useLocalStorageOptions } from '@shared/hooks/useLocalStorageOptions';

import {
  CHANNEL_LIST_DEFAULT_OPTIONS,
  CHANNEL_LIST_FILTER_NAME_LIST,
  CHANNEL_LIST_SORT_FIELD,
  CHANNEL_LIST_SORT_ORDER,
  ChannelListOptionsModel,
} from './channel-list/channel-list.config';

// /////////////////////////////////////////////////////////////////////////////

export interface ChannelListContextProps {
  getPaginatedQueryParameters: GetChannelsPaginatedModel;
  setGetPaginatedQueryParameters: Dispatch<
    SetStateAction<GetChannelsPaginatedModel>
  >;
  getPaginatedLimitList: number[];
  columnOptions: ChannelListOptionsModel;
  setColumnOptions: Dispatch<SetStateAction<ChannelListOptionsModel>>;
}

export const ChannelListContext = createContext<ChannelListContextProps | null>(
  null,
);

export const useChannelListContext = (): ChannelListContextProps => {
  const context = useContext(ChannelListContext);

  if (!context) {
    throw new Error(
      'useChannelListContext must be used within ChannelListContext.Provider',
    );
  }

  return context;
};

interface ChannelListContextProviderProps {
  children: React.ReactNode;
}

export const ChannelListContextProvider = ({
  children,
}: ChannelListContextProviderProps): React.JSX.Element => {
  const [getPaginatedQueryParameters, setGetPaginatedQueryParameters] =
    useUrlSyncedFilters<GetChannelsPaginatedModel>(
      {
        limit: TABLE_LIMITS_OPTIONS.at(-1) ?? TABLE_LIMIT_OPTION_MIN,
        offset: 0,
        channelOcppStatus: [],
        customerId: [],
        locationId: [],
        networkId: [],
        tariffId: [],
        textToSearch: '',
        sortField: CHANNEL_LIST_SORT_FIELD,
        sortOrder: CHANNEL_LIST_SORT_ORDER,
      },
      [
        'limit',
        'offset',
        'sortField',
        'sortOrder',
        'textToSearch',
        ...CHANNEL_LIST_FILTER_NAME_LIST,
      ],
      CHANNEL_FILTERS_LSK,
    );

  const [columnOptions, setColumnOptions] =
    useLocalStorageOptions<ChannelListOptionsModel>(
      CHANNEL_LIST_DEFAULT_OPTIONS,
      CHANNEL_LIST_OPTIONS_LSK,
    );

  const data = useMemo(() => {
    return {
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      getPaginatedLimitList: TABLE_LIMITS_OPTIONS,
      columnOptions,
      setColumnOptions,
    };
  }, [
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    columnOptions,
    setColumnOptions,
  ]);

  return (
    <ChannelListContext.Provider value={data}>
      {children}
    </ChannelListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterChannelListContext =
  createContext<DeepPartialSkipArrayKey<GetChannelsPaginatedModel> | null>(
    null,
  );

export const useFilterChannelListContext =
  (): DeepPartialSkipArrayKey<GetChannelsPaginatedModel> => {
    const context = useContext(FilterChannelListContext);

    if (!context) {
      throw new Error(
        'useFilterChannelListContext must be used within FilterChannelListContext.Provider',
      );
    }

    return context;
  };

interface FilterChannelListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetChannelsPaginatedModel>;
  children: React.ReactNode;
}

export const FilterChannelListContextProvider = ({
  data,
  children,
}: FilterChannelListContextProviderProps): React.JSX.Element => {
  return (
    <FilterChannelListContext.Provider value={data}>
      {children}
    </FilterChannelListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterSearchChannelListContext =
  createContext<DeepPartialSkipArrayKey<GetChannelsPaginatedModel> | null>(
    null,
  );

export const useFilterSearchChannelListContext =
  (): DeepPartialSkipArrayKey<GetChannelsPaginatedModel> => {
    const context = useContext(FilterSearchChannelListContext);

    if (!context) {
      throw new Error(
        'useFilterSearchChannelListContext must be used within FilterSearchChannelListContext.Provider',
      );
    }

    return context;
  };

interface FilterSearchChannelListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetChannelsPaginatedModel>;
  children: React.ReactNode;
}

export const FilterSearchChannelListContextProvider = ({
  data,
  children,
}: FilterSearchChannelListContextProviderProps): React.JSX.Element => {
  return (
    <FilterSearchChannelListContext.Provider value={data}>
      {children}
    </FilterSearchChannelListContext.Provider>
  );
};
